// @flow
import { createHashFromHref } from "../../utils/helpers/createHash";

import BaseModel from "../base/BaseModel";
import LinkCollection from "../links/LinkCollection";
import Href from "../href/Href";
import LinkModel from "../links/LinkModel";
import { retrieveText } from "../../utils";
import type { ModelOptions } from "../types";

/**
 * Link to a concept
 */
export default class SubSectionModel extends BaseModel {
  _entryDate: ISO_DATE | null;
  _links: LinkCollection;

  /**
   */
  constructor(
    data: Object,
    entryDate: ISO_DATE | null,
    modelOptions?: ModelOptions,
  ) {
    super(data, {}, modelOptions);

    this._entryDate = entryDate;
  }

  /**
   */
  get referred(): boolean {
    return this.data.referred;
  }

  /**
   */
  get id(): string {
    return this.data.id || this.data._id;
  }

  /**
   * Retrieve key of subsection
   */
  get key(): ?string {
    return this.id;
  }

  /**
   * Retrieve html body
   */
  get body(): string | null {
    const body = this.getData("body", "");
    return retrieveText(body);
  }

  /**
   * Retrieve number of section
   */
  get number(): string | null {
    return this.data.number ?? null;
  }

  /**
   * Retrieve label of section
   */
  get label(): ?string {
    return this.data.label;
  }

  /**
   * Get sub sections
   */
  get subSections(): Array<SubSectionModel> {
    return this.data.subSections
      ? this.data.subSections.map(
          (subSection) =>
            new SubSectionModel(subSection, this.entryDate, this.modelOptions),
        )
      : [];
  }

  /**
   * Retrieve links of section
   */
  get links(): LinkCollection {
    if (!this._links) {
      this._links = new LinkCollection(
        this.data._links,
        this.contributions._links,
      );
    }

    return this._links;
  }

  /**
   * Get self link of model
   */
  get selflink(): LinkModel | null {
    return this.links.getLinkByKey("self");
  }

  /**
   * Return default self link of resource
   */
  get selfhref(): Href | null {
    return this.selflink ? this.selflink.href : null;
  }

  /**
   */
  get entryDate(): ISO_DATE | null {
    return this._entryDate;
  }

  /**
   */
  get relatedConceptsHrefs(): Array<Href> {
    const hrefs = [];
    if (this.relatedConceptsHref) {
      hrefs.push(this.relatedConceptsHref);
    }

    this.subSections.forEach((subSection) => {
      hrefs.push(...subSection.relatedConceptsHrefs);
    });

    return hrefs;
  }

  /**
   * Get related concepts link
   */
  get relatedConceptsHref(): Href | null {
    const relatedConceptsLink = this.links.getLinkByKey("relatedConcepts");

    if (relatedConceptsLink) {
      const { href } = relatedConceptsLink;

      if (href.hash.includes("/relatedConcepts")) {
        const firstPart = href.path.substring(0, href.path.lastIndexOf("/"));
        href.path = firstPart + "/" + href.hash;
        href.hash = href.hash.includes("#")
          ? href.hash.substring(href.hash.indexOf("#"))
          : "";
      }

      href.addParameter("entryDate", this.entryDate);
      href.setReferenceHash(this.referenceHash);

      return href;
    }

    return null;
  }

  /**
   */
  get referenceHash(): number {
    const selfhref = this.selfhref || new Href();
    return createHashFromHref(selfhref);
  }

  /**
   */
  getReferredSections(): Array<SubSectionModel> {
    const sections = [];

    for (const subSection of this.subSections) {
      if (subSection.referred) {
        sections.push(subSection);
      }

      const referredChilds = subSection.getReferredSections();
      sections.push(...referredChilds);
    }

    return sections;
  }
}
